import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import {
    Box, Card, Stack, TextField, MenuItem,
    Button, Grid, LinearProgress, Alert, ToggleButton, ToggleButtonGroup,
    Drawer, Typography, Divider, IconButton, Tooltip, Chip, Snackbar,
    Collapse, Dialog, DialogTitle, DialogContent, DialogActions,
    FormControlLabel, Checkbox, CircularProgress
} from "@mui/material";
import {
    FileDownload as ExportIcon,
    Refresh as RefreshIcon,
    EditNote as OverrideIcon,
    Close as CloseIcon,
    CheckCircle as ApproveIcon,
    Cancel as RejectIcon,
    Undo as RevokeIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    ViewColumn as ViewColumnIcon,
    SaveOutlined as SaveIcon,
    RestartAlt as RestartAltIcon,
    TableRows as DensityIcon
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppHeader from '../../components/AppHeader';
import { buildHierarchicalList } from '../../components/staff/constants';

const MONTHLY_REVIEW_GRID_KEY = "monthly-attendance-review";

const CATEGORY_DEFINITIONS = [
    {
        id: 'staff',
        title: 'Staff & Readiness',
        colIds: ['missing_days', 'employee_number', 'full_name']
    },
    {
        id: 'org',
        title: 'Org Structure',
        colIds: ['branch_id', 'org_node_id']
    },
    {
        id: 'attendance',
        title: 'Attendance & Overtime',
        colIds: [
            'expected_days', 'processed_days', 'days_present', 'total_late_mins',
            'total_ot_mins', 'normal_ot_mins', 'rest_ot_mins', 'holiday_ot_mins', 'extended_ot_mins'
        ]
    },
    {
        id: 'allowances',
        title: 'Variable Allowances & Incentives',
        colIds: [
            'meal_allowance_eligible_days', 'total_meal_allw',
            'transport_allowance_eligible_days', 'total_transport_allw',
            'ot_allowance_eligible_days', 'total_ot_allw',
            'special_allowance_eligible_days', 'total_special_allw',
            'early_arrival_eligible_days', 'total_early_arrival_allw',
            'is_pai_eligible', 'pai_allowance_amount'
        ]
    },
    {
        id: 'deductions',
        title: 'Deductions & Infractions',
        colIds: ['total_penalties', 'violation_count']
    },
    {
        id: 'leaves',
        title: 'Absence & Leave Summary',
        colIds: [
            'unresolved_absence_days', 'paid_absence_days', 'unpaid_absence_days',
            'leave_al', 'leave_sl', 'leave_cl', 'leave_ul'
        ]
    }
];

const buildColumnLabels = (colDefs = []) => {
    const labels = {};
    const traverse = (defs) => {
        defs.forEach(col => {
            if (col.children) {
                traverse(col.children);
            } else {
                const colId = col.colId || col.field;
                if (colId) {
                    labels[colId] = col.headerName || col.field || colId;
                }
            }
        });
    };
    traverse(colDefs);
    return labels;
};

const getDescendantNodeIds = (nodeId, nodesList) => {
    const ids = [nodeId];
    const traverse = (currentId) => {
        for (const node of nodesList) {
            if (node.parent_id === currentId) {
                ids.push(node.id);
                traverse(node.id);
            }
        }
    };
    traverse(nodeId);
    return ids;
};

const OVERRIDE_COMPONENTS = [
    { code: 'TOTAL_OT_MINS', label: 'Total OT', unit: 'MINUTES', field: 'total_ot_mins' },
    { code: 'NORMAL_OT_MINS', label: 'Normal OT', unit: 'MINUTES', field: 'normal_ot_mins' },
    { code: 'REST_OT_MINS', label: 'Rest Day OT', unit: 'MINUTES', field: 'rest_ot_mins' },
    { code: 'HOLIDAY_OT_MINS', label: 'Holiday OT', unit: 'MINUTES', field: 'holiday_ot_mins' },
    { code: 'EXTENDED_OT_MINS', label: 'Extended OT', unit: 'MINUTES', field: 'extended_ot_mins' },
    { code: 'MEAL_ALLOWANCE_DAYS', label: 'Meal Allowance Days', unit: 'DAYS', field: 'meal_allowance_days' },
    { code: 'TRANSPORT_ALLOWANCE_DAYS', label: 'Transport Allowance Days', unit: 'DAYS', field: 'transport_allowance_days' },
    { code: 'EARLY_ARRIVAL_DAYS', label: 'Early Arrival Days', unit: 'DAYS', field: 'early_arrival_eligible_days' },
    { code: 'PERFECT_ATTENDANCE_ELIGIBLE', label: 'Perfect Attendance Eligible', unit: 'BOOLEAN', field: 'is_pai_eligible' },
    { code: 'PAID_ABSENCE_DAYS', label: 'Paid Absence Days', unit: 'DAYS', field: 'paid_absence_days' },
    { code: 'UNPAID_ABSENCE_DAYS', label: 'Unpaid Absence Days', unit: 'DAYS', field: 'unpaid_absence_days' },
    { code: 'LATE_EARLY_PENALTY_AMOUNT', label: 'Late/Early Penalty Amount', unit: 'AMOUNT', field: 'total_penalties' }
];

const componentMap = OVERRIDE_COMPONENTS.reduce((acc, item) => ({ ...acc, [item.code]: item }), {});

const valueForComponent = (row, config) => {
    if (!row || !config?.field) return null;
    if (config.unit === 'BOOLEAN') return row[config.field] ? 1 : 0;
    const value = Number(row[config.field] || 0);
    return Number.isFinite(value) ? value : null;
};

const formatOverrideValue = (value, unit) => {
    if (value === null || value === undefined || value === '') return '-';
    if (unit === 'BOOLEAN') return Number(value) === 1 ? 'Eligible' : 'No';
    if (unit === 'MINUTES') return `${Number(value || 0).toLocaleString()} m`;
    if (unit === 'DAYS') return `${Number(value || 0).toFixed(2)} d`;
    if (unit === 'AMOUNT') return `${Number(value || 0).toLocaleString()} MMK`;
    return Number(value || 0).toLocaleString();
};

const statusColor = (status) => {
    if (status === 'APPROVED') return 'success';
    if (status === 'PENDING') return 'warning';
    if (status === 'REJECTED') return 'error';
    if (status === 'REVOKED') return 'default';
    return 'default';
};

const MonthlyReview = ({ setIsLoggedIn }) => {
    const gridRef = useRef();
    const [loading, setLoading] = useState(false);
    const [rowData, setRowData] = useState([]);
    const [filters, setFilters] = useState({
        month: new Date().getMonth() + 1, // 1-12
        year: new Date().getFullYear(),
    });
    const [searchText, setSearchText] = useState('');
    const [selectedBranch, setSelectedBranch] = useState('');
    const [selectedOrgNode, setSelectedOrgNode] = useState('');
    const [otDisplayUnit, setOtDisplayUnit] = useState('minutes');
    const [branches, setBranches] = useState([]);
    const [orgNodes, setOrgNodes] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [overrideRows, setOverrideRows] = useState([]);
    const [overrideLoading, setOverrideLoading] = useState(false);
    const [overrideSaving, setOverrideSaving] = useState(false);
    const [overrideForm, setOverrideForm] = useState({
        component_code: 'TOTAL_OT_MINS',
        override_value: '',
        reason: ''
    });
    const [message, setMessage] = useState({ open: false, severity: 'success', text: '' });

    // Accordion visibility states
    const [accState, setAccState] = useState(() => {
        try {
            const saved = localStorage.getItem('monthly_review_acc_state');
            return saved ? JSON.parse(saved) : { params: true, warning: true };
        } catch {
            return { params: true, warning: true };
        }
    });

    const toggleAccordion = (section) => {
        setAccState(prev => {
            const next = { ...prev, [section]: !prev[section] };
            try { localStorage.setItem('monthly_review_acc_state', JSON.stringify(next)); } catch {}
            return next;
        });
    };

    // Column visibility and density states
    const columnLabelsRef = useRef({});
    const [columnDialogOpen, setColumnDialogOpen] = useState(false);
    const [groupedColumns, setGroupedColumns] = useState([]);
    const [gridPreferenceLoading, setGridPreferenceLoading] = useState(false);
    const [gridPreferenceSaving, setGridPreferenceSaving] = useState(false);
    const [rowDensity, setRowDensity] = useState(() => localStorage.getItem('monthly_review_row_density') || 'compact');

    const months = [
        { value: 1, label: 'January' }, { value: 2, label: 'February' },
        { value: 3, label: 'March' }, { value: 4, label: 'April' },
        { value: 5, label: 'May' }, { value: 6, label: 'June' },
        { value: 7, label: 'July' }, { value: 8, label: 'August' },
        { value: 9, label: 'September' }, { value: 10, label: 'October' },
        { value: 11, label: 'November' }, { value: 12, label: 'December' }
    ];

    const currentYear = new Date().getFullYear();
    const years = [currentYear - 1, currentYear, currentYear + 1];

    const formatOtValue = useCallback((value) => {
        const minutes = Number(value || 0);
        if (!minutes) return '-';
        if (otDisplayUnit === 'hours') {
            return `${(minutes / 60).toFixed(2)} h`;
        }
        return `${minutes.toLocaleString()} m`;
    }, [otDisplayUnit]);

    const requestConfig = useCallback(() => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const tenant = localStorage.getItem('tenant') || '';
        return {
            headers: {
                Authorization: `Bearer ${token}`,
                'X-Tenant': tenant
            },
            withCredentials: true
        };
    }, []);

    const showMessage = useCallback((severity, text) => {
        setMessage({ open: true, severity, text });
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/monthly-attendance-summary`, {
                params: filters,
                ...requestConfig()
            });
            if (response.data.success) {
                setRowData(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching summary:", error);
            showMessage('error', error.response?.data?.message || 'Failed to load monthly summary');
        } finally {
            setLoading(false);
        }
    }, [filters, requestConfig, showMessage]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Fetch branches and departments list on load
    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const token = sessionStorage.getItem('token') || localStorage.getItem('token');
                const tenant = localStorage.getItem('tenant') || '';
                const config = {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'X-Tenant': tenant
                    },
                    withCredentials: true
                };
                const [branchRes, nodeRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL1}/som/branches`, config),
                    axios.get(`${process.env.REACT_APP_API_URL1}/som/nodes`, config)
                ]);
                if (branchRes.data?.success) setBranches(branchRes.data.data);
                if (nodeRes.data?.success) setOrgNodes(buildHierarchicalList(nodeRes.data.data));
            } catch (error) {
                console.error("Error fetching filter options:", error);
            }
        };
        fetchFilters();
    }, []);

    const branchMap = useMemo(() => {
        return branches.reduce((acc, b) => ({ ...acc, [b.id]: b.branch_name }), {});
    }, [branches]);

    const orgNodeMap = useMemo(() => {
        return orgNodes.reduce((acc, n) => ({ ...acc, [n.id]: `${n.node_name} (${n.node_code})` }), {});
    }, [orgNodes]);

    const filteredRowData = useMemo(() => {
        return rowData.filter(row => {
            // Search Text filter
            if (searchText) {
                const term = searchText.toLowerCase().trim();
                const nameMatch = row.full_name ? row.full_name.toLowerCase().includes(term) : false;
                const empIdMatch = row.employee_number ? row.employee_number.toLowerCase().includes(term) : false;
                const codeMatch = row.staff_code ? row.staff_code.toLowerCase().includes(term) : false;
                if (!nameMatch && !empIdMatch && !codeMatch) return false;
            }
            // Branch filter
            if (selectedBranch) {
                if (row.branch_id !== Number(selectedBranch)) return false;
            }
            // SOM/Org Node filter
            if (selectedOrgNode) {
                const targetNodeId = Number(selectedOrgNode);
                const allowedNodeIds = getDescendantNodeIds(targetNodeId, orgNodes);
                if (!allowedNodeIds.includes(row.org_node_id)) return false;
            }
            return true;
        });
    }, [rowData, searchText, selectedBranch, selectedOrgNode, orgNodes]);

    const notReadyStaffCount = useMemo(() => {
        return filteredRowData.filter(row => row.is_attendance_ready === false || row.missing_days > 0 || row.incomplete_days > 0).length;
    }, [filteredRowData]);

    const onBtnExport = useCallback(() => {
        if (gridRef.current?.api) {
            const api = gridRef.current.api;
            const columnKeys = api.getColumnState?.()
                ? api.getColumnState().filter(c => !c.hide && c.colId !== 'action').map(c => c.colId)
                : undefined;
            api.exportDataAsCsv({
                fileName: `monthly_attendance_review_${filters.year}_${filters.month}.csv`,
                columnKeys
            });
        }
    }, [filters]);

    const fetchOverrides = useCallback(async (staff) => {
        if (!staff) return;
        setOverrideLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/input-overrides`, {
                params: {
                    month: filters.month,
                    year: filters.year,
                    staff_id: staff.staff_id
                },
                ...requestConfig()
            });
            setOverrideRows(response.data?.data || []);
        } catch (error) {
            console.error('Error fetching overrides:', error);
            showMessage('error', error.response?.data?.message || 'Failed to load overrides');
        } finally {
            setOverrideLoading(false);
        }
    }, [filters, requestConfig, showMessage]);

    const openOverrideDrawer = useCallback((staff) => {
        setSelectedStaff(staff);
        setOverrideForm({ component_code: 'TOTAL_OT_MINS', override_value: '', reason: '' });
        fetchOverrides(staff);
    }, [fetchOverrides]);

    const closeOverrideDrawer = useCallback(() => {
        setSelectedStaff(null);
        setOverrideRows([]);
        setOverrideForm({ component_code: 'TOTAL_OT_MINS', override_value: '', reason: '' });
    }, []);

    const activeOverrides = useMemo(() => {
        return overrideRows.reduce((acc, row) => {
            if (['PENDING', 'APPROVED'].includes(row.status)) acc[row.component_code] = row;
            return acc;
        }, {});
    }, [overrideRows]);

    const selectedComponent = componentMap[overrideForm.component_code] || OVERRIDE_COMPONENTS[0];

    const saveOverride = useCallback(async () => {
        if (!selectedStaff) return;
        const component = componentMap[overrideForm.component_code];
        const calculatedValue = valueForComponent(selectedStaff, component);
        if (overrideForm.override_value === '' || !overrideForm.reason.trim()) {
            showMessage('error', 'Override value and reason are required');
            return;
        }
        setOverrideSaving(true);
        try {
            await axios.post(`${process.env.REACT_APP_API_URL1}/sm/payroll/input-overrides`, {
                month: filters.month,
                year: filters.year,
                staff_id: selectedStaff.staff_id,
                component_code: overrideForm.component_code,
                calculated_value: calculatedValue,
                override_value: Number(overrideForm.override_value),
                reason: overrideForm.reason.trim()
            }, requestConfig());
            showMessage('success', 'Override submitted for approval');
            setOverrideForm({ component_code: overrideForm.component_code, override_value: '', reason: '' });
            await fetchOverrides(selectedStaff);
        } catch (error) {
            console.error('Save override error:', error);
            showMessage('error', error.response?.data?.message || 'Failed to save override');
        } finally {
            setOverrideSaving(false);
        }
    }, [fetchOverrides, filters, overrideForm, requestConfig, selectedStaff, showMessage]);

    const actOnOverride = useCallback(async (row, action) => {
        let body = {};
        if (action === 'reject' || action === 'revoke') {
            const reason = window.prompt(action === 'reject' ? 'Rejection reason' : 'Revoke reason');
            if (!reason?.trim()) return;
            body.reason = reason.trim();
        }
        setOverrideSaving(true);
        try {
            await axios.post(`${process.env.REACT_APP_API_URL1}/sm/payroll/input-overrides/${row.id}/${action}`, body, requestConfig());
            showMessage('success', `Override ${action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'revoked'}`);
            await fetchOverrides(selectedStaff);
            await fetchData();
        } catch (error) {
            console.error(`Override ${action} error:`, error);
            showMessage('error', error.response?.data?.message || `Failed to ${action} override`);
        } finally {
            setOverrideSaving(false);
        }
    }, [fetchData, fetchOverrides, requestConfig, selectedStaff, showMessage]);

    // Refresh categorized column options based on current grid state
    const refreshColumnOptions = useCallback(() => {
        const api = gridRef.current?.api;
        if (!api?.getColumnState) return;
        const labels = columnLabelsRef.current || {};
        const colStates = api.getColumnState();
        const stateMap = {};
        colStates.forEach(col => {
            stateMap[col.colId] = !col.hide;
        });

        const matchedColIds = new Set();
        const groups = CATEGORY_DEFINITIONS.map(cat => {
            const items = [];
            colStates.forEach(col => {
                if (col.colId === 'action') return; // Action is permanently visible
                const isMember = cat.colIds?.includes(col.colId);
                if (isMember) {
                    matchedColIds.add(col.colId);
                    items.push({
                        colId: col.colId,
                        label: labels[col.colId] || col.colId,
                        visible: stateMap[col.colId] !== false
                    });
                }
            });
            return {
                ...cat,
                items
            };
        }).filter(cat => cat.items.length > 0);

        const otherItems = [];
        colStates.forEach(col => {
            if (col.colId === 'action') return;
            if (!matchedColIds.has(col.colId)) {
                otherItems.push({
                    colId: col.colId,
                    label: labels[col.colId] || col.colId,
                    visible: stateMap[col.colId] !== false
                });
            }
        });
        if (otherItems.length > 0) {
            groups.push({
                id: 'other',
                title: 'Other Columns',
                items: otherItems
            });
        }

        setGroupedColumns(groups);
    }, []);

    const handleToggleColumn = useCallback((colId, visible) => {
        const api = gridRef.current?.api;
        if (!api) return;
        if (api.setColumnsVisible) {
            api.setColumnsVisible([colId], visible);
        } else if (api.setColumnVisible) {
            api.setColumnVisible(colId, visible);
        }
        refreshColumnOptions();
    }, [refreshColumnOptions]);

    const handleToggleGroup = useCallback((groupItems, visible) => {
        const api = gridRef.current?.api;
        if (!api) return;
        const colIds = groupItems.map(item => item.colId);
        if (api.setColumnsVisible) {
            api.setColumnsVisible(colIds, visible);
        }
        refreshColumnOptions();
    }, [refreshColumnOptions]);

    const applyGridPreference = useCallback((gridState) => {
        const api = gridRef.current?.api;
        if (!api || !gridState) return;
        if (Array.isArray(gridState.columnState) && api.applyColumnState) {
            api.applyColumnState({ state: gridState.columnState, applyOrder: true });
        }
        if (gridState.filterModel && api.setFilterModel) {
            api.setFilterModel(gridState.filterModel);
        }
        if (gridState.rowDensity) {
            setRowDensity(gridState.rowDensity);
            localStorage.setItem('monthly_review_row_density', gridState.rowDensity);
        }
        refreshColumnOptions();
    }, [refreshColumnOptions]);

    const loadGridPreference = useCallback(async () => {
        try {
            setGridPreferenceLoading(true);
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const headers = { Authorization: `Bearer ${token}`, 'X-Tenant': tenant };
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/users/grid-preferences/${MONTHLY_REVIEW_GRID_KEY}`, {
                params: { view_name: "default" },
                withCredentials: true,
                headers,
            });
            if (res.data?.success && res.data?.data?.grid_state) {
                applyGridPreference(res.data.data.grid_state);
            } else {
                refreshColumnOptions();
            }
        } catch (err) {
            console.error("Failed to load monthly review grid preference:", err);
            refreshColumnOptions();
        } finally {
            setGridPreferenceLoading(false);
        }
    }, [applyGridPreference, refreshColumnOptions]);

    const handleSaveGridPreference = useCallback(async () => {
        const api = gridRef.current?.api;
        if (!api?.getColumnState) return;
        try {
            setGridPreferenceSaving(true);
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const headers = { Authorization: `Bearer ${token}`, 'X-Tenant': tenant };
            await axios.post(`${process.env.REACT_APP_API_URL1}/users/grid-preferences/${MONTHLY_REVIEW_GRID_KEY}`, {
                view_name: "default",
                grid_state: {
                    version: 1,
                    columnState: api.getColumnState(),
                    filterModel: api.getFilterModel ? api.getFilterModel() : null,
                    rowDensity: rowDensity,
                    savedAt: new Date().toISOString(),
                },
            }, {
                withCredentials: true,
                headers,
            });
            showMessage('success', "Monthly review grid view saved successfully.");
        } catch (err) {
            console.error("Failed to save monthly review grid preference:", err);
            showMessage('error', err.response?.data?.message || "Could not save column view.");
        } finally {
            setGridPreferenceSaving(false);
        }
    }, [rowDensity, showMessage]);

    const handleResetGridPreference = useCallback(async () => {
        const api = gridRef.current?.api;
        if (!api) return;
        try {
            setGridPreferenceSaving(true);
            if (api.resetColumnState) api.resetColumnState();
            if (api.setFilterModel) api.setFilterModel(null);
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const headers = { Authorization: `Bearer ${token}`, 'X-Tenant': tenant };
            await axios.delete(`${process.env.REACT_APP_API_URL1}/users/grid-preferences/${MONTHLY_REVIEW_GRID_KEY}`, {
                params: { view_name: "default" },
                withCredentials: true,
                headers,
            });
            refreshColumnOptions();
            showMessage('success', "Reset to default grid view.");
        } catch (err) {
            console.error("Failed to reset grid preference:", err);
            showMessage('error', "Could not reset grid view.");
        } finally {
            setGridPreferenceSaving(false);
        }
    }, [refreshColumnOptions, showMessage]);

    const columnDefs = useMemo(() => [
        {
            headerName: "Action",
            colId: "action",
            pinned: 'left',
            width: 72,
            sortable: false,
            filter: false,
            cellRenderer: params => (
                <Tooltip title="Payroll input overrides">
                    <IconButton
                        size="small"
                        onClick={() => openOverrideDrawer(params.data)}
                        sx={{
                            minHeight: 24,
                            height: 24,
                            width: 28,
                            p: 0,
                            borderRadius: 1,
                            color: '#1a237e',
                            '&:hover': { bgcolor: '#e8eaf6' }
                        }}
                    >
                        <OverrideIcon sx={{ fontSize: '1.15rem' }} />
                    </IconButton>
                </Tooltip>
            )
        },
        {
            headerName: "Readiness",
            colId: "missing_days",
            field: "missing_days",
            pinned: 'left',
            width: 140,
            cellRenderer: params => {
                const missing = params.value || 0;
                const incomplete = Number(params.data?.incomplete_days || 0);
                const unresolved = Number(params.data?.unresolved_absence_days || 0);
                if (missing > 0) {
                    return <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>⚠️ Missing {missing}d</span>;
                }
                if (incomplete > 0) {
                    return <span style={{ color: '#ed6c02', fontWeight: 'bold' }}>⚠️ Incomplete {incomplete}d</span>;
                }
                if (unresolved > 0) {
                    return <span style={{ color: '#ed6c02', fontWeight: 'bold' }}>⚠️ Unresolved {unresolved}d</span>;
                }
                return <span style={{ color: '#2e7d32' }}>✅ Ready</span>;
            }
        },
        {
            headerName: "Staff Info",
            pinned: 'left',
            children: [
                { field: "employee_number", colId: "employee_number", headerName: "Emp ID", width: 120 },
                { field: "full_name", colId: "full_name", headerName: "Name", width: 180 },
            ]
        },
        {
            headerName: "Org Structure",
            children: [
                {
                    headerName: "Branch",
                    field: "branch_id",
                    colId: "branch_id",
                    width: 140,
                    valueFormatter: params => branchMap[params.value] || '-'
                },
                {
                    headerName: "Department",
                    field: "org_node_id",
                    colId: "org_node_id",
                    width: 150,
                    valueFormatter: params => orgNodeMap[params.value] || '-'
                }
            ]
        },
        {
            headerName: "Attendance",
            children: [
                { field: "expected_days", colId: "expected_days", headerName: "Expected", width: 100 },
                { field: "processed_days", colId: "processed_days", headerName: "Processed", width: 110 },
                { field: "days_present", colId: "days_present", headerName: "Present", width: 100 },
                {
                    field: "total_late_mins",
                    colId: "total_late_mins",
                    headerName: "Late (m)",
                    width: 100,
                    cellStyle: params => params.value > 0 ? { color: '#d32f2f', fontWeight: 'bold' } : null
                },
                { field: "total_ot_mins", colId: "total_ot_mins", headerName: "Total OT", width: 120, valueFormatter: params => formatOtValue(params.value) },
                { field: "normal_ot_mins", colId: "normal_ot_mins", headerName: "Normal OT", width: 120, valueFormatter: params => formatOtValue(params.value) },
                { field: "rest_ot_mins", colId: "rest_ot_mins", headerName: "Rest OT", width: 110, valueFormatter: params => formatOtValue(params.value) },
                { field: "holiday_ot_mins", colId: "holiday_ot_mins", headerName: "Holiday OT", width: 120, valueFormatter: params => formatOtValue(params.value) },
                { field: "extended_ot_mins", colId: "extended_ot_mins", headerName: "Extended OT", width: 130, valueFormatter: params => formatOtValue(params.value) },
            ]
        },
        {
            headerName: "Variable Allowances (MMK)",
            children: [
                { field: "meal_allowance_eligible_days", colId: "meal_allowance_eligible_days", headerName: "Meal Qty", width: 90 },
                { field: "total_meal_allw", colId: "total_meal_allw", headerName: "Meal", width: 100, valueFormatter: p => p.value ? Number(p.value).toLocaleString() : '-' },
                { field: "transport_allowance_eligible_days", colId: "transport_allowance_eligible_days", headerName: "Trans Qty", width: 100 },
                { field: "total_transport_allw", colId: "total_transport_allw", headerName: "Trans", width: 100, valueFormatter: p => p.value ? Number(p.value).toLocaleString() : '-' },
                { field: "ot_allowance_eligible_days", colId: "ot_allowance_eligible_days", headerName: "OT Meal Qty", width: 110 },
                { field: "total_ot_allw", colId: "total_ot_allw", headerName: "OT Meal", width: 100, valueFormatter: p => p.value ? Number(p.value).toLocaleString() : '-' },
                { field: "special_allowance_eligible_days", colId: "special_allowance_eligible_days", headerName: "Spec Qty", width: 100 },
                { field: "total_special_allw", colId: "total_special_allw", headerName: "Spec", width: 100, valueFormatter: p => p.value ? Number(p.value).toLocaleString() : '-' },
            ]
        },
        {
            headerName: "Incentive Basis",
            children: [
                {
                    field: "early_arrival_eligible_days",
                    colId: "early_arrival_eligible_days",
                    headerName: "Early Arrival Days",
                    width: 150,
                    valueFormatter: params => Number(params.value || 0) > 0 ? Number(params.value).toLocaleString() : '-'
                },
                {
                    field: "total_early_arrival_allw",
                    colId: "total_early_arrival_allw",
                    headerName: "Early Arrival MMK",
                    width: 130,
                    cellStyle: params => params.value > 0 ? { color: '#0d9488', fontWeight: 'bold' } : null,
                    valueFormatter: params => params.value > 0 ? Number(params.value).toLocaleString() : '-'
                },
                {
                    field: "is_pai_eligible",
                    colId: "is_pai_eligible",
                    headerName: "Perfect Att.",
                    width: 120,
                    cellRenderer: params => params.value ? 'Eligible' : 'No',
                    cellStyle: { textAlign: 'center' }
                },
                {
                    field: "pai_allowance_amount",
                    colId: "pai_allowance_amount",
                    headerName: "Perfect Att. MMK",
                    width: 140,
                    cellStyle: params => params.value > 0 ? { color: '#0d9488', fontWeight: 'bold' } : null,
                    valueFormatter: params => params.value > 0 ? Number(params.value).toLocaleString() : '-'
                },
            ]
        },
        {
            headerName: "Deductions",
            children: [
                {
                    field: "total_penalties",
                    colId: "total_penalties",
                    headerName: "Late Penalties",
                    width: 140,
                    cellStyle: params => params.value > 0 ? { color: '#d32f2f', fontWeight: 'bold' } : null,
                    valueFormatter: p => p.value ? Number(p.value).toLocaleString() : '-'
                },
                { field: "violation_count", colId: "violation_count", headerName: "Infractions", width: 110 },
            ]
        },
        {
            headerName: "Absence Resolutions (Days)",
            children: [
                { 
                    field: "unresolved_absence_days", 
                    colId: "unresolved_absence_days",
                    headerName: "Unresolved", 
                    width: 110, 
                    cellStyle: params => params.value > 0 ? { color: '#ed6c02', fontWeight: 'bold' } : null 
                },
                { 
                    field: "paid_absence_days", 
                    colId: "paid_absence_days",
                    headerName: "Paid", 
                    width: 90,
                    valueFormatter: params => params.value > 0 ? Number(params.value).toFixed(2) : '-'
                },
                { 
                    field: "unpaid_absence_days", 
                    colId: "unpaid_absence_days",
                    headerName: "Unpaid", 
                    width: 90,
                    valueFormatter: params => params.value > 0 ? Number(params.value).toFixed(2) : '-'
                },
            ]
        },
        {
            headerName: "Leave Summary (Days)",
            children: [
                { colId: "leave_al", headerName: "AL", valueGetter: params => params.data.leaves?.AL || 0, width: 70 },
                { colId: "leave_sl", headerName: "SL", valueGetter: params => params.data.leaves?.SL || params.data.leaves?.MC || 0, width: 70 },
                { colId: "leave_cl", headerName: "CL", valueGetter: params => params.data.leaves?.CL || 0, width: 70 },
                { colId: "leave_ul", headerName: "UL", valueGetter: params => params.data.leaves?.UL || params.data.leaves?.UP || 0, width: 70 },
            ]
        }
    ], [branchMap, formatOtValue, openOverrideDrawer, orgNodeMap]);

    useEffect(() => {
        columnLabelsRef.current = buildColumnLabels(columnDefs);
        if (gridRef.current?.api) {
            refreshColumnOptions();
        }
    }, [columnDefs, refreshColumnOptions]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f4f7fe' }}>
            <AppHeader title="Monthly Attendance Review" setIsLoggedIn={setIsLoggedIn} />

            <Box sx={{ p: { xs: 1.5, md: 2 }, flexGrow: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* SECTION 1: REVIEW PARAMETERS & FILTERS ACCORDION */}
                <Card sx={{
                    borderRadius: 2,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                    border: '1px solid #E2E8F0',
                    overflow: 'hidden'
                }}>
                    <Box sx={{
                        p: 1.25,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        bgcolor: '#FFFFFF',
                        borderBottom: accState.params ? '1px solid #F1F5F9' : 'none'
                    }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ cursor: 'pointer', overflow: 'hidden' }} onClick={() => toggleAccordion('params')}>
                            <IconButton size="small" sx={{ p: 0.5, color: '#475569' }}>
                                {accState.params ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                                Review Parameters & Filters
                            </Typography>
                            <Chip
                                size="small"
                                label={`${months.find(m => m.value === Number(filters.month))?.label || filters.month} ${filters.year}`}
                                sx={{ bgcolor: '#EFF6FF', color: '#1D4ED8', fontWeight: 600, fontSize: '0.75rem', height: 22 }}
                            />
                            {selectedBranch && (
                                <Chip
                                    size="small"
                                    label={`Branch: ${branchMap[selectedBranch] || selectedBranch}`}
                                    sx={{ bgcolor: '#F1F5F9', color: '#475569', fontSize: '0.75rem', height: 22 }}
                                />
                            )}
                            {selectedOrgNode && (
                                <Chip
                                    size="small"
                                    label={`Node: ${orgNodeMap[selectedOrgNode] || selectedOrgNode}`}
                                    sx={{ bgcolor: '#F1F5F9', color: '#475569', fontSize: '0.75rem', height: 22 }}
                                />
                            )}
                            {searchText && (
                                <Chip
                                    size="small"
                                    label={`Search: "${searchText}"`}
                                    sx={{ bgcolor: '#F1F5F9', color: '#475569', fontSize: '0.75rem', height: 22 }}
                                />
                            )}
                        </Stack>

                        {/* Always accessible action buttons */}
                        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<RefreshIcon />}
                                onClick={fetchData}
                                sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, py: 0.5 }}
                            >
                                Refresh
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<ExportIcon />}
                                onClick={onBtnExport}
                                sx={{
                                    borderRadius: 1.5,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    bgcolor: '#1a237e',
                                    py: 0.5,
                                    '&:hover': { bgcolor: '#0d47a1' }
                                }}
                            >
                                Export CSV
                            </Button>
                        </Stack>
                    </Box>

                    <Collapse in={accState.params}>
                        <Box sx={{ p: 1.5, bgcolor: '#FAFAFA' }}>
                            <Grid container spacing={1.5} alignItems="center">
                                <Grid item xs={12} sm={2} md={1.5}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Month"
                                        value={filters.month}
                                        onChange={(e) => setFilters({ ...filters, month: e.target.value })}
                                        size="small"
                                    >
                                        {months.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={2} md={1.2}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Year"
                                        value={filters.year}
                                        onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                                        size="small"
                                    >
                                        {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={3} md={2.8}>
                                    <TextField
                                        fullWidth
                                        label="Search Staff"
                                        placeholder="Name, ID or Code"
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3} md={2.5}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Branch"
                                        value={selectedBranch}
                                        onChange={(e) => setSelectedBranch(e.target.value)}
                                        size="small"
                                    >
                                        <MenuItem value="">All Branches</MenuItem>
                                        {branches.map((b) => <MenuItem key={b.id} value={b.id}>{b.branch_name}</MenuItem>)}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={4} md={3}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="SOM / Org Node"
                                        value={selectedOrgNode}
                                        onChange={(e) => setSelectedOrgNode(e.target.value)}
                                        size="small"
                                    >
                                        <MenuItem value="">All Departments</MenuItem>
                                        {orgNodes.map((n) => (
                                            <MenuItem key={n.id} value={n.id} sx={{ pl: 2 + (n._depth || 0) * 2 }}>
                                                {n._depth > 0 && <span style={{ color: '#94a3b8', marginRight: 4 }}>{'└'}</span>}
                                                {n.node_name}{n.level_type ? ` (${n.level_type})` : ''} ({n.node_code})
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            </Grid>
                        </Box>
                    </Collapse>
                </Card>

                {/* SECTION 2: ATTENDANCE READINESS & WARNING ACCORDION */}
                {notReadyStaffCount > 0 && (
                    <Card sx={{
                        borderRadius: 2,
                        boxShadow: '0 2px 8px rgba(237, 108, 2, 0.08)',
                        border: '1px solid #FED7AA',
                        bgcolor: '#FFFBEB',
                        overflow: 'hidden'
                    }}>
                        <Box sx={{
                            px: 1.5,
                            py: 0.75,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer'
                        }} onClick={() => toggleAccordion('warning')}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <IconButton size="small" sx={{ p: 0.25, color: '#D97706' }}>
                                    {accState.warning ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </IconButton>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#B45309', fontSize: '0.8125rem' }}>
                                    ⚠️ Attention Required: {notReadyStaffCount} staff members have missing or incomplete attendance records
                                </Typography>
                            </Stack>
                            <Typography variant="caption" sx={{ color: '#92400E', fontWeight: 600 }}>
                                {accState.warning ? "Hide details" : "Show details & resolution steps"}
                            </Typography>
                        </Box>

                        <Collapse in={accState.warning}>
                            <Box sx={{ px: 2, pb: 1.25, pt: 0.25, borderTop: '1px solid #FEF3C7' }}>
                                <Typography variant="body2" sx={{ color: '#92400E', fontSize: '0.8rem', lineHeight: 1.4 }}>
                                    Please run <strong>"Process Attendance Daily"</strong> to resolve incomplete records before finalizing payroll. Perfect Attendance Incentives for these staff have been automatically disabled until resolved.
                                </Typography>
                            </Box>
                        </Collapse>
                    </Card>
                )}

                {/* SECTION 3: TOOLBAR WITH KPI, OT DISPLAY, DENSITY, COLUMNS & VIEW CONTROLS */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                            <span style={{ color: '#64748B' }}>Showing: </span>
                            <span style={{ color: '#0F172A' }}>{filteredRowData.length} of {rowData.length} staff</span>
                        </Typography>
                        {notReadyStaffCount > 0 ? (
                            <Chip
                                size="small"
                                label={`${notReadyStaffCount} Not Ready`}
                                sx={{ bgcolor: '#FEE2E2', color: '#DC2626', fontWeight: 700, fontSize: '0.75rem', height: 22 }}
                            />
                        ) : (
                            <Chip
                                size="small"
                                label="All Attendance Ready"
                                sx={{ bgcolor: '#DCFCE7', color: '#16A34A', fontWeight: 700, fontSize: '0.75rem', height: 22 }}
                            />
                        )}
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                        {/* OT Unit toggle */}
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748B' }}>OT Unit:</Typography>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={otDisplayUnit}
                                onChange={(event, value) => value && setOtDisplayUnit(value)}
                                sx={{ height: 28 }}
                            >
                                <ToggleButton value="minutes" sx={{ textTransform: 'none', px: 1, py: 0, fontSize: '0.75rem' }}>Min</ToggleButton>
                                <ToggleButton value="hours" sx={{ textTransform: 'none', px: 1, py: 0, fontSize: '0.75rem' }}>Hours</ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>

                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 20, alignSelf: 'center' }} />

                        {/* Density toggle */}
                        <Tooltip title={`Row Density: ${rowDensity === 'compact' ? 'Compact 36px (Click for Standard 44px)' : 'Standard 44px (Click for Compact 36px)'}`}>
                            <Button
                                variant="outlined"
                                color="inherit"
                                size="small"
                                startIcon={<DensityIcon fontSize="small" />}
                                onClick={() => {
                                    const next = rowDensity === 'compact' ? 'standard' : 'compact';
                                    setRowDensity(next);
                                    localStorage.setItem('monthly_review_row_density', next);
                                    setTimeout(() => {
                                        gridRef.current?.api?.resetRowHeights();
                                    }, 0);
                                }}
                                sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, py: 0.4, fontSize: '0.75rem' }}
                            >
                                {rowDensity === 'compact' ? 'Compact (36px)' : 'Standard (44px)'}
                            </Button>
                        </Tooltip>

                        {/* Columns Visibility */}
                        <Tooltip title="Show or hide columns by category">
                            <Button
                                variant="outlined"
                                color="inherit"
                                size="small"
                                startIcon={<ViewColumnIcon />}
                                onClick={() => {
                                    refreshColumnOptions();
                                    setColumnDialogOpen(true);
                                }}
                                sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, py: 0.4, fontSize: '0.75rem' }}
                            >
                                Columns
                            </Button>
                        </Tooltip>

                        {/* Save View */}
                        <Tooltip title="Save current column visibility, widths, order, and filters">
                            <span>
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    size="small"
                                    startIcon={<SaveIcon />}
                                    disabled={gridPreferenceSaving}
                                    onClick={handleSaveGridPreference}
                                    sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, py: 0.4, fontSize: '0.75rem' }}
                                >
                                    Save View
                                </Button>
                            </span>
                        </Tooltip>

                        {/* Reset View */}
                        <Tooltip title="Reset to default columns and layout">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={handleResetGridPreference}
                                    disabled={gridPreferenceSaving}
                                    sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', p: 0.5 }}
                                >
                                    <RestartAltIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Box>

                {loading && <LinearProgress sx={{ borderRadius: 1 }} />}

                {/* AG GRID CARD (Dynamic flex height fills remaining screen space) */}
                <Card sx={{
                    borderRadius: 2,
                    boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
                    border: '1px solid #E2E8F0',
                    flexGrow: 1,
                    minHeight: 300,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box
                        className="ag-theme-quartz"
                        sx={{
                            width: '100%',
                            height: '100%',
                            flexGrow: 1,
                            overscrollBehaviorX: 'none',
                            '& .ag-header-cell-label': { fontSize: '0.8125rem' },
                            '& .ag-header-group-cell-label': { fontSize: '0.8125rem', fontWeight: 700 },
                            '& .ag-cell': { fontSize: '0.8125rem', display: 'flex', alignItems: 'center' }
                        }}
                    >
                        <AgGridReact
                            ref={gridRef}
                            rowData={filteredRowData}
                            columnDefs={columnDefs}
                            defaultColDef={{
                                sortable: true,
                                filter: true,
                                resizable: true
                            }}
                            pagination={true}
                            paginationPageSize={20}
                            paginationPageSizeSelector={[10, 20, 50, 100]}
                            animateRows={true}
                            rowHeight={rowDensity === 'compact' ? 36 : 44}
                            headerHeight={rowDensity === 'compact' ? 36 : 44}
                            groupHeaderHeight={rowDensity === 'compact' ? 34 : 40}
                            onGridReady={() => {
                                loadGridPreference();
                            }}
                            onColumnMoved={refreshColumnOptions}
                            onColumnVisible={refreshColumnOptions}
                        />
                    </Box>
                </Card>
            </Box>

            {/* GROUPED COLUMN VISIBILITY DIALOG */}
            <Dialog
                open={columnDialogOpen}
                onClose={() => setColumnDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight={700}>Attendance Review Column Visibility</Typography>
                    <Chip
                        label={`${groupedColumns.reduce((sum, g) => sum + g.items.filter(i => i.visible).length, 0)} visible`}
                        size="small"
                        color="primary"
                    />
                </DialogTitle>
                <DialogContent dividers sx={{ p: 2 }}>
                    {gridPreferenceLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3, justifyContent: 'center' }}>
                            <CircularProgress size={24} />
                            <Typography variant="body2" color="text.secondary">Loading saved view...</Typography>
                        </Box>
                    ) : (
                        <Stack spacing={2}>
                            {groupedColumns.map(group => {
                                const allVisible = group.items.every(i => i.visible);

                                return (
                                    <Box key={group.id} sx={{ p: 1.5, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                                {group.title} ({group.items.filter(i => i.visible).length}/{group.items.length})
                                            </Typography>
                                            <Button
                                                size="small"
                                                onClick={() => handleToggleGroup(group.items, !allVisible)}
                                                sx={{ textTransform: 'none', py: 0.25, fontSize: '0.75rem', fontWeight: 600 }}
                                            >
                                                {allVisible ? "Hide All" : "Show All"}
                                            </Button>
                                        </Box>
                                        <Grid container spacing={0.5}>
                                            {group.items.map(item => (
                                                <Grid item xs={12} sm={6} key={item.colId}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                size="small"
                                                                checked={item.visible}
                                                                onChange={e => handleToggleColumn(item.colId, e.target.checked)}
                                                            />
                                                        }
                                                        label={<Typography variant="body2">{item.label}</Typography>}
                                                        sx={{ m: 0 }}
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 2, py: 1.5 }}>
                    <Button onClick={() => setColumnDialogOpen(false)} color="inherit">Close</Button>
                    <Button
                        variant="outlined"
                        startIcon={<RestartAltIcon />}
                        disabled={gridPreferenceSaving}
                        onClick={handleResetGridPreference}
                    >
                        Reset Default
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={gridPreferenceSaving}
                        onClick={() => {
                            handleSaveGridPreference();
                            setColumnDialogOpen(false);
                        }}
                        color="primary"
                    >
                        Save View
                    </Button>
                </DialogActions>
            </Dialog>

            <Drawer
                anchor="right"
                open={!!selectedStaff}
                onClose={closeOverrideDrawer}
                PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, borderLeft: '1px solid #e2e8f0' } }}
            >
                <Box sx={{ p: 3, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                Payroll Input Overrides
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                                {selectedStaff?.employee_number || selectedStaff?.staff_code} · {selectedStaff?.full_name}
                            </Typography>
                        </Box>
                        <IconButton onClick={closeOverrideDrawer}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </Box>

                <Box sx={{ p: 3 }}>
                    <Stack spacing={2}>
                        <TextField
                            select
                            label="Component"
                            size="small"
                            value={overrideForm.component_code}
                            onChange={(e) => setOverrideForm({ ...overrideForm, component_code: e.target.value, override_value: '' })}
                        >
                            {OVERRIDE_COMPONENTS.map(component => (
                                <MenuItem key={component.code} value={component.code}>{component.label}</MenuItem>
                            ))}
                        </TextField>

                        <Grid container spacing={1.5}>
                            <Grid item xs={4}>
                                <Box sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>Calculated</Typography>
                                    <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>
                                        {formatOverrideValue(valueForComponent(selectedStaff, selectedComponent), selectedComponent.unit)}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={4}>
                                <Box sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>Active Override</Typography>
                                    <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>
                                        {formatOverrideValue(activeOverrides[overrideForm.component_code]?.override_value, selectedComponent.unit)}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={4}>
                                <Box sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>Payroll Final</Typography>
                                    <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>
                                        {formatOverrideValue(
                                            activeOverrides[overrideForm.component_code]?.status === 'APPROVED'
                                                ? activeOverrides[overrideForm.component_code]?.override_value
                                                : valueForComponent(selectedStaff, selectedComponent),
                                            selectedComponent.unit
                                        )}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        {selectedComponent.unit === 'BOOLEAN' ? (
                            <TextField
                                select
                                label="Override Value"
                                size="small"
                                value={overrideForm.override_value}
                                onChange={(e) => setOverrideForm({ ...overrideForm, override_value: e.target.value })}
                            >
                                <MenuItem value={1}>Eligible</MenuItem>
                                <MenuItem value={0}>No</MenuItem>
                            </TextField>
                        ) : (
                            <TextField
                                label={`Override Value (${selectedComponent.unit})`}
                                size="small"
                                type="number"
                                value={overrideForm.override_value}
                                onChange={(e) => setOverrideForm({ ...overrideForm, override_value: e.target.value })}
                            />
                        )}

                        <TextField
                            label="Reason"
                            size="small"
                            multiline
                            minRows={2}
                            value={overrideForm.reason}
                            onChange={(e) => setOverrideForm({ ...overrideForm, reason: e.target.value })}
                        />

                        <Button
                            variant="contained"
                            onClick={saveOverride}
                            disabled={overrideSaving}
                            sx={{ alignSelf: 'flex-start', textTransform: 'none', bgcolor: '#1a237e' }}
                        >
                            Submit Override
                        </Button>
                    </Stack>

                    <Divider sx={{ my: 3 }} />

                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                        <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>Override History</Typography>
                        {overrideLoading && <Typography variant="caption" sx={{ color: '#64748b' }}>Loading...</Typography>}
                    </Stack>

                    <Stack spacing={1.5}>
                        {overrideRows.length === 0 && (
                            <Alert severity="info" variant="outlined">No overrides for this employee and period.</Alert>
                        )}
                        {overrideRows.map(row => {
                            const config = componentMap[row.component_code] || { label: row.component_code, unit: row.unit };
                            return (
                                <Box key={row.id} sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff' }}>
                                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                                        <Box>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>{config.label}</Typography>
                                                <Chip size="small" label={row.status} color={statusColor(row.status)} variant="outlined" />
                                            </Stack>
                                            <Typography variant="body2" sx={{ color: '#475569', mt: 0.75 }}>
                                                {formatOverrideValue(row.calculated_value, config.unit)} → {formatOverrideValue(row.override_value, config.unit)}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.75 }}>
                                                {row.reason}
                                            </Typography>
                                        </Box>
                                        <Stack direction="row" spacing={0.5}>
                                            {row.status === 'PENDING' && (
                                                <>
                                                    <Tooltip title="Approve">
                                                        <IconButton size="small" color="success" disabled={overrideSaving} onClick={() => actOnOverride(row, 'approve')}>
                                                            <ApproveIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Reject">
                                                        <IconButton size="small" color="error" disabled={overrideSaving} onClick={() => actOnOverride(row, 'reject')}>
                                                            <RejectIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                            {row.status === 'APPROVED' && (
                                                <Tooltip title="Revoke">
                                                    <IconButton size="small" color="warning" disabled={overrideSaving} onClick={() => actOnOverride(row, 'revoke')}>
                                                        <RevokeIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            </Drawer>

            <Snackbar
                open={message.open}
                autoHideDuration={5000}
                onClose={() => setMessage({ ...message, open: false })}
            >
                <Alert severity={message.severity} onClose={() => setMessage({ ...message, open: false })} sx={{ width: '100%' }}>
                    {message.text}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default MonthlyReview;
